<!-- src/App.vue -->
<template>
  <component :is="layout">
    <div id="app-container" class="flex flex-col min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30">
      
      <!-- 1. MASTER COMMAND NAVIGATION (UPGRADED WITH FLOWI BACKDROP-BLUR GLASS) -->
      <nav v-if="!isBlankLayout" class="bg-white/80 backdrop-blur-3xl relative sticky top-0 z-50 border-b border-slate-200/40 no-print transition-all duration-300">
        <div class="max-w-7xl mx-auto px-4 sm:px-0 flex items-center justify-between h-20">
          
          <!-- Logo Index (Updated with Cursor Pointer & Native Force-Refresh Click Handshake) -->
          <div class="flex-shrink-0 cursor-pointer flex items-center gap-3 select-none" @click="forceAppRefresh">
            <img src="/images/sentinel-safe-logo.png" alt="Sentinel Safe Logo" class="h-7 w-auto transition-transform active:scale-95">
            <!-- Shimmering Montserrat Brand Text (Flowi Sister-Style) -->
            <span class="font-montserrat sentinel-shimmer font-black tracking-tighter text-2xl">Sentinel Safe</span>
          </div>

          <!-- Mobile Actions -->
          <div class="md:hidden flex items-center gap-3">
            <div v-if="userStore.isLoggedIn" class="flex items-center gap-2">
              <button @click="openInstallModal('android')" class="p-2 text-slate-500 hover:text-[#20FCF1] transition-all">
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.3414C17.135 15.3414 16.821 15.0274 16.821 14.6394C16.821 14.2514 17.135 13.9374 17.523 13.9374C17.911 13.9374 18.225 14.2514 18.225 14.6394C18.225 15.0274 17.911 15.3414 17.523 15.3414ZM6.477 15.3414C6.089 15.3414 5.775 15.0274 5.775 14.6394C5.775 14.2514 6.089 13.9374 6.477 13.9374C6.865 13.9374 7.179 14.2514 7.179 14.6394C7.179 15.0274 6.865 15.3414 6.477 15.3414ZM17.973 10.3704L19.742 7.3074C19.866 7.0914 19.792 6.8154 19.576 6.6914C19.36 6.5674 19.084 6.6414 12.225 8.9184C10.446 8.9184 8.767 9.2944 7.286 9.9674L5.49 6.8574C5.366 6.6414 5.09 6.5674 4.874 6.6914C4.658 6.8154 4.584 7.0914 4.708 7.3074L6.477 10.3704C3.125 12.1854 0.825 15.5454 0.225 19.4624H23.775C23.175 15.5454 20.875 12.1854 17.973 10.3704Z"/></svg>
              </button>
              <button @click="openInstallModal('ios')" class="p-2 text-slate-500 hover:text-[#20FCF1] transition-colors">
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.89 1.22-2.13 1.08-3.37-1.07.04-2.36.71-3.13 1.6-.69.79-1.29 2.05-1.13 3.26 1.19.09 2.39-.6 3.18-1.49z"/></svg>
              </button>
            </div>
            
            <button @click="toggleMobileMenu" class="relative p-2 text-[#20FCF1] bg-[#0a1630] rounded-xl border border-slate-700 active:scale-95 transition-all">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <div v-if="totalPendingActions > 0" class="absolute -top-1 -right-1 h-4 w-4 bg-[#EF4444] rounded-full border-2 border-[#0A1630] pulse-badge"></div>
            </button>
          </div>

          <!-- Desktop Command Tier -->
          <div class="hidden md:flex items-center gap-4">
            <template v-if="userStore.isLoggedIn">
              
              <!-- 👁️ CLIENT IMPERSONATION SWITCHER (Super Admins Only) -->
              <select
                v-if="userStore.userProfile?.role === 'super-admin' || userStore.role === 'super-admin'"
                :value="userStore.userProfile?.impersonating ? (userStore.userProfile?.clientId || 'superadmin-home') : 'superadmin-home'"
                @change="handleImpersonateClient($event.target.value)"
                class="bg-[#0A1630] border border-slate-800 text-[#20FCF1] text-[10px] font-black uppercase tracking-wider rounded-xl px-4 py-2 outline-none cursor-pointer hover:border-[#20FCF1]/40 transition-colors mr-2 shadow-md"
              >
                <option value="superadmin-home">🛡️ Super Admin (Home)</option>
                <option v-for="client in clientsList" :key="client.id" :value="client.id">
                  👁️ View As: {{ client.name }}
                </option>
              </select>

              <div class="flex items-center gap-2 border-r border-slate-200 pr-4 mr-2">
                <button @click="openInstallModal('android')" class="nav-icon-btn-premium">
                  <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.3414C17.135 15.3414 16.821 15.0274 16.821 14.6394C16.821 14.2514 17.135 13.9374 17.523 13.9374C17.911 13.9374 18.225 14.2514 18.225 14.6394C18.225 15.0274 17.911 15.3414 17.523 15.3414ZM6.477 15.3414C6.089 15.3414 5.775 15.0274 5.775 14.6394C5.775 14.2514 6.089 13.9374 6.477 13.9374C6.865 13.9374 7.179 14.2514 7.179 14.6394C7.179 15.0274 6.865 15.3414 6.477 15.3414ZM17.973 10.3704L19.742 7.3074C19.866 7.0914 19.792 6.8154 19.576 6.6914C19.36 6.5674 19.084 6.6414 12.225 8.9184C10.446 8.9184 8.767 9.2944 7.286 9.9674L5.49 6.8574C5.366 6.6414 5.09 6.5674 4.874 6.6914C4.658 6.8154 4.584 7.0914 4.708 7.3074L6.477 10.3704C3.125 12.1854 0.825 15.5454 0.225 19.4624H23.775C23.175 15.5454 20.875 12.1854 17.973 10.3704Z"/></svg>
                </button>
                <button @click="openInstallModal('ios')" class="nav-icon-btn-premium">
                  <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.89 1.22-2.13 1.08-3.37-1.07.04-2.36.71-3.13 1.6-.69.79-1.29 2.05-1.13 3.26 1.19.09 2.39-.6 3.18-1.49z"/></svg>
                </button>
              </div>

              <router-link to="/dashboard" class="nav-link-premium">Command</router-link>
              <router-link v-if="userStore.hasAdminAccess" to="/client-admin" class="nav-link-premium">Admin</router-link>
              <router-link v-if="userStore.role === 'super-admin'" to="/super-admin" class="nav-link-premium">Super</router-link>
              
              <router-link to="/profile" class="relative group block rounded-full">
                <!-- 🛡️ OPTION B CONSTRAINED PROFILE AVATAR CONTAINER -->
                <div class="relative flex items-center p-0.5 rounded-2xl border-2 border-slate-200 group-hover:border-[#20FCF1] transition-all bg-white shadow-lg">
                  <img v-if="userPhotoUrl" :src="userPhotoUrl" alt="Profile" class="h-10 w-10 rounded-xl object-cover bg-white">
                  <div v-else class="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-[#20FCF1] font-black text-xs select-none">
                    {{ userInitial }}
                  </div>
                  
                  <div v-if="totalPendingActions > 0" class="absolute -top-3 -right-3 h-6 w-6 bg-[#EF4444] text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-[#0A1630] shadow-xl pulse-badge z-[60]">
                    {{ totalPendingActions }}
                  </div>
                </div>
              </router-link>
            </template>
          </div>
        </div>

        <div class="absolute bottom-0 left-0 w-full h-[4px] bg-[#0A1630] overflow-hidden">
          <div class="header-scan-pulse h-full w-1/4 bg-gradient-to-r from-transparent via-[#20FCF1]/80 to-transparent"></div>
        </div>

        <!-- MOBILE COMMAND OVERLAY -->
        <div v-if="isMobileMenuOpen" class="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md animate-fadeIn rounded-b-[32px] shadow-2xl overflow-hidden">
          <div v-if="userStore.isLoggedIn" class="p-6 space-y-4">
            
            <!-- 👁️ CLIENT IMPERSONATION SWITCHER (Mobile View) -->
            <div v-if="userStore.userProfile?.role === 'super-admin' || userStore.role === 'super-admin'" class="flex flex-col gap-1 text-left mb-2">
              <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Impersonate Workspace</span>
              <select
                :value="userStore.userProfile?.impersonating ? (userStore.userProfile?.clientId || 'superadmin-home') : 'superadmin-home'"
                @change="handleImpersonateClient($event.target.value)"
                class="w-full bg-[#0A1630] border border-slate-800 text-[#20FCF1] text-xs font-black uppercase tracking-wider rounded-xl px-4 py-3 outline-none cursor-pointer"
              >
                <option value="superadmin-home">🛡️ Super Admin (Home)</option>
                <option v-for="client in clientsList" :key="client.id" :value="client.id">
                  👁️ View As: {{ client.name }}
                </option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <router-link to="/dashboard" class="nav-btn-premium text-center justify-center" @click="closeMobileMenu">Command</router-link>
              <router-link v-if="userStore.hasAdminAccess" to="/client-admin" class="nav-btn-premium text-center justify-center" @click="closeMobileMenu">Admin Hub</router-link>
            </div>
            
            <router-link to="/profile" class="flex items-center justify-between p-5 bg-slate-50 border border-slate-200 rounded-2xl animate-fadeIn" @click="closeMobileMenu">
              <div class="flex items-center space-x-4">
                <img v-if="userPhotoUrl" :src="userPhotoUrl" alt="My Profile" class="h-12 w-12 rounded-xl object-cover border-2 border-slate-200">
                <div v-else class="h-12 w-12 bg-white rounded-xl flex items-center justify-center text-[#20FCF1] font-black text-xs select-none">
                  {{ userInitial }}
                </div>
                <div class="flex flex-col text-left">
                  <span class="font-black uppercase text-xs tracking-widest text-[#0A1630]">Identity Registry</span>
                  <span class="text-[8px] font-bold text-slate-400 uppercase tracking-widest">View Profile & Actions</span>
                </div>
              </div>
              <div v-if="totalPendingActions > 0" class="bg-[#EF4444] text-white px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest pulse-badge shadow-lg">
                {{ totalPendingActions }} Alerts
              </div>
            </router-link>
          </div>
        </div>
      </nav>
      
      <SubHeader v-if="userStore.isLoggedIn && !isBlankLayout && !$route.meta.hideSubHeader" />
      
      <main class="flex-grow pt-4 pb-10">
        <!-- 🛡️ ZERO-REFACTOR CLIENT IMPERSONATION DYNAMIC KEY -->
        <router-view :key="userStore.clientId" />
      </main>

      <!-- PREMIUM GOVERNANCE FOOTER (EXACT FLOWI SISTER STYLE MATCH) -->
      <footer class="bg-white py-12 border-t border-slate-100 mt-auto no-print w-full relative z-10 flex flex-col items-center gap-6 select-none">
        <img 
          src="/images/pixel-logo.png" 
          alt="Pixel Perfection Logo" 
          class="h-10 object-contain select-none pointer-events-none" 
        />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-center space-y-6">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] leading-none">
            Copyright &copy; {{ currentYear }} Sentinel Safe™. All Rights Reserved.
          </p>
          <div class="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em] leading-none space-y-3">
            <p>Sentinel Safe™ is operated and managed by Get Pixel Perfection Limited.</p>
            <p>Get Pixel Perfection Limited is a company registered in England and Wales. Company Number: 16854093.</p>
            <p>UK GDPR Compliant • <a href="https://pixelperfection.co.uk/privacy.html" target="_blank" rel="noopener noreferrer" class="hover:text-slate-500 transition-colors duration-200">Privacy Policy</a></p>
          </div>
        </div>
      </footer>

      <!-- Floating Assets (RESTORED HERE!) -->
      <AdminFAB v-if="userStore.isAdmin" :isAdmin="true" />
      <InstallGuideModal :isOpen="isInstallModalOpen" :initialTab="installTargetPlatform" @close="isInstallModalOpen = false" />
      <TutorialVideoModal v-if="showOnboardingVideo" />
      <a href="https://pixelperfection.co.uk" target="_blank" rel="noopener noreferrer" class="created-by-link no-print uppercase">Get Pixel Perfection</a>
      
      <!-- GLOBALLY INTEGRATED AI CHAT BOT ASSISTANT (RESTRICTED TO ADMINS/MANAGERS) -->
      <AiHelpAssistant v-if="userStore.isLoggedIn && userStore.hasAdminAccess" />
      
    </div>
  </component>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import SubHeader from '@/components/SubHeader.vue';
import BlankLayout from '@/Layouts/BlankLayout.vue';
import AdminFAB from '@/components/AdminFAB.vue';
import InstallGuideModal from '@/components/modals/InstallGuideModal.vue'; 
import TutorialVideoModal from '@/components/modals/TutorialVideoModal.vue';
import AiHelpAssistant from '@/components/common/AiHelpAssistant.vue';

const route = useRoute();
const userStore = useUserStore();
const isMobileMenuOpen = ref(false);
const isInstallModalOpen = ref(false);
const installTargetPlatform = ref('android');

const clientsList = ref([]);

const totalPendingActions = computed(() => userStore.unreadNotificationCount);
const currentYear = new Date().getFullYear();
const isBlankLayout = computed(() => route.meta.layout === 'Blank');
const layout = computed(() => isBlankLayout.value ? BlankLayout : 'div');
const userPhotoUrl = computed(() => userStore.userProfile?.photoUrl || '');

// Dynamic user initial resolution matching active session state
const userInitial = computed(() => {
  const name = userStore.userProfile?.firstName || '';
  return name ? name.charAt(0).toUpperCase() : 'U';
});

// Guide video pops up for any user who is NOT "active" to support new accounts
const showOnboardingVideo = computed(() => {
  return userStore.isLoggedIn && 
         userStore.userProfile?.status?.toLowerCase() !== 'active' && 
         !isBlankLayout.value;
});

const toggleMobileMenu = () => isMobileMenuOpen.value = !isMobileMenuOpen.value;
const closeMobileMenu = () => isMobileMenuOpen.value = false;
const openInstallModal = (platform) => { 
  installTargetPlatform.value = platform; 
  isInstallModalOpen.value = true; 
};

// Forces a native document reload to fetch the latest server-compiled app assets
const forceAppRefresh = () => {
  window.location.href = window.location.origin;
};

// 👁️ FETCH REGISTERED WORKSPACES FOR SUPER ADMIN SWITCHER
const fetchClientsForImpersonation = async () => {
  if (userStore.role === 'super-admin' || userStore.userProfile?.role === 'super-admin') {
    try {
      const querySnapshot = await getDocs(collection(db, 'clients'));
      clientsList.value = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        name: docSnap.data().clientName || 'Unnamed Client'
      })).sort((a, b) => a.name.localeCompare(b.name));
    } catch (err) {
      console.warn("Telemetry: Failed to load clients registry for impersonation.", err);
    }
  }
};

// 👁️ SECURE MULTI-TENANT CONTEXT SWAPPER WITH IMMMEDIATE TOKEN RE-MINTING
const handleImpersonateClient = async (targetClientId) => {
  const profile = userStore.userProfile;
  const uid = userStore.user?.uid || auth.currentUser?.uid;
  if (!uid || !profile) return;
  
  try {
    const profileRef = doc(db, 'userProfiles', uid);
    
    // Back up original credentials if not already stored
    const backupClientId = profile.originalClientId || profile.clientId || 'superadmin-home';
    const backupClientName = profile.originalClientName || profile.clientName || 'Super Admin Space';
    
    if (targetClientId === 'superadmin-home') {
      await updateDoc(profileRef, {
        clientId: backupClientId === 'superadmin-home' ? null : backupClientId,
        clientName: backupClientName,
        impersonating: false
      });

      // 🛡️ FORCE ID TOKEN RE-MINTING TO SWAP CLAIMS INSTANTLY [3.2]
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        await userStore.fetchUser(auth.currentUser);
      }

      alert("Returned to Super Admin Workspace");
    } else {
      const targetClient = clientsList.value.find(c => c.id === targetClientId);
      await updateDoc(profileRef, {
        originalClientId: backupClientId,
        originalClientName: backupClientName,
        clientId: targetClientId,
        clientName: targetClient ? targetClient.name : 'Impersonated Workspace',
        impersonating: true
      });

      // 🛡️ FORCE ID TOKEN RE-MINTING TO SWAP CLAIMS INSTANTLY [3.2]
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        await userStore.fetchUser(auth.currentUser);
      }

      alert(`Now viewing workspace as: ${targetClient ? targetClient.name : 'Target Client'}`);
    }
  } catch (err) {
    console.error("Failed to switch client context:", err);
    alert("Failed to switch client context: " + err.message);
  }
};

watch(() => userStore.isAuthReady, (ready) => {
  if (ready) {
    fetchClientsForImpersonation();
  }
}, { immediate: true });

watch(() => userStore.userProfile?.role, (role) => {
  if (role === 'super-admin') {
    fetchClientsForImpersonation();
  }
});

onMounted(() => {
  if (userStore.isAuthReady) {
    fetchClientsForImpersonation();
  }
});
</script>

<style>
#app { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }

/* 🛡️ LOAD MONTSERRAT GOOGLE FONT FOR SHIMMERING BRAND TEXT */
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@900&display=swap');

.font-montserrat {
  font-family: 'Montserrat', sans-serif;
}

/* 🛡️ FLOWI SISTER-STYLE HIGH-FIDELITY GRADIENT SHIMMER */
.sentinel-shimmer {
  background: linear-gradient(120deg, #0A1630 30%, #20FCF1 50%, #bae6fd 60%, #20FCF1 70%, #0A1630 90%);
  background-size: 200% auto;
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  animation: sentinelShine 8s linear infinite;
  position: relative;
  display: inline-block;
}

@keyframes sentinelShine {
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
}

.header-scan-pulse { animation: pulse-move 4s infinite ease-in-out; position: absolute; }
@keyframes pulse-move { 0% { transform: translateX(-150%); } 100% { transform: translateX(450%); } }

.nav-btn-premium { @apply inline-flex items-center px-5 py-2.5 bg-[#1E293B] border-2 border-slate-700 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-300 transition-all duration-300 shadow-lg no-underline active:scale-95; }
.nav-btn-premium:hover { @apply border-[#20FCF1] bg-[#20FCF1]/10 text-white; }
.nav-btn-premium.router-link-exact-active { @apply border-[#20FCF1] text-[#20FCF1] bg-[#20FCF1]/5; }
.nav-icon-btn-premium { @apply p-2.5 rounded-xl text-slate-500 hover:text-[#20FCF1] hover:bg-slate-100 transition-all duration-300; }

.nav-link-premium { @apply text-xs font-black uppercase tracking-widest text-slate-500 hover:text-[#0A1630] transition-colors duration-200 no-underline px-3 py-1.5; }
.nav-link-premium.router-link-exact-active { @apply text-[#0A1630] border-b-2 border-[#20FCF1]; }

.pulse-badge { animation: badge-pulse-red 2s infinite; }
@keyframes badge-pulse-red { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); } 70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }

.created-by-link { position: fixed; bottom: 2.5rem; left: 2.5rem; z-index: 1000; transform-origin: bottom left; white-space: nowrap; color: #CBD5E1; font-size: 0.65rem; font-weight: 900; letter-spacing: 0.25em; text-decoration: none; transform: rotate(-90deg); opacity: 0.5; transition: all 0.3s ease-in-out; }
.created-by-link:hover { opacity: 1; color: #FD5E00; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>